import _debounce from 'lodash.debounce'
import _throttle from 'lodash.throttle'

export const wait = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

export function notEmpty<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined
}

/**
 * Creates a throttled function that only invokes func at most once per every wait milliseconds.
 * The throttled function comes with a cancel method to cancel delayed func invocations
 * and a flush method to immediately invoke them. Provide options to indicate whether func
 * should be invoked on the leading and/or trailing edge of the wait timeout.
 * The func is invoked with the last arguments provided to the throttled function.
 * Subsequent calls to the throttled function return the result of the last func invocation.
 *
 * https://lodash.com/docs/4.17.15#throttle
 *
 * Debounce vs Throttle: https://css-tricks.com/debouncing-throttling-explained-examples/
 */
export const throttle: <T>(
  fn: T,
  wait?: number,
  options?: { leading?: boolean; trailing?: boolean },
) => T = _throttle

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have
 * elapsed since the last time the debounced function was invoked.
 * The debounced function comes with a cancel method to cancel delayed func
 * invocations and a flush method to immediately invoke them.
 * Provide options to indicate whether func should be invoked on the leading
 * and/or trailing edge of the wait timeout.
 * The func is invoked with the last arguments provided to the debounced function.
 * Subsequent calls to the debounced function return the result of the last func invocation.
 *
 * https://lodash.com/docs/4.17.15#debounce
 *
 * Debounce vs Throttle: https://css-tricks.com/debouncing-throttling-explained-examples/
 */
export const debounce: <T>(
  fn: T,
  wait?: number,
  options?: { maxWait?: number; leading?: boolean; trailing?: boolean },
) => T = _debounce

/**
 * Converts Unicode escape sequences within a string to their corresponding characters.
 * This is necessary for decoding JSON strings that contain escaped with Django template's `escapejs`.
 *
 * @example
 * ```
 * const jsonStr = "{\u0022.hope\u0022: \u0022I hope he feels better soon!\u0022}"
 * const result = unescapejs(jsonStr);
 * console.log(result); // '{"hope": "I hope he feels better soon!"}'
 * console.log(JSON.parse(result)); // {hope: "I hope he feels better soon!"}
 * ```
 */
export function unescapejs(str: string) {
  return str.replace(/\\u[\dA-F]{4}/gi, function (match) {
    return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
  })
}

export function safeJsonParse<T>(str: string): T | null {
  try {
    return JSON.parse(str)
  } catch (e) {
    console.error('safeJsonParse: Error parsing JSON', e)
    return null
  }
}

/**
 * Invokes a function immediately after wait milliseconds have elapsed.
 * If the page is hidden before the time has elapsed, the function will not be invoked.
 * The page state is considered hidden when a user navigates to a new page, switches tabs, closes the tab, minimizes or closes the browser, or, on mobile, switches from the browser to a different app. (https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event#usage_notes)
 */
export function delayWhileVisible<T extends () => void>(fn: T, wait: number) {
  const timeout = setTimeout(() => {
    fn()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, wait)

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      clearTimeout(timeout)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
}
